import { FC } from 'react';
import {
  Grid,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
  Box,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Work,
  School,
  VerifiedUser,
  SupervisorAccount,
  Language,
  Settings,
} from '@mui/icons-material';
import { Avatar } from '@mui/material';
import {
  CancelButton,
  EditButton,
  SaveButton,
} from 'src/views/components/simple/AllButtons';
import {
  useProfilePersonalDetailsActions,
  useProfilePersonalDetailsState,
} from '../../contexts/profilePersonalDetails.context';
import EditPersonalDetailsView from './subviews/editPersonalDetails.view';
import { GENDER } from 'src/apis/users/users.interfaces';
import { IProfilePersonalDetailsApiResponse } from 'src/apis/profile/profilePersonalDetails.interfaces';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useLanguage } from 'src/context/language.context';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

const PersonalDetailsView: FC = () => {
  const { translateText, i18n } = useTranslation();
  const { personalDetails, isLoading, isEditMode } =
    useProfilePersonalDetailsState();
  const { setIsEditMode, setEditedPersonalDetails } =
    useProfilePersonalDetailsActions();
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();

  // Use i18n.language as the source of truth, fallback to currentLanguage, then 'en'
  // Ensure the selected language is a valid option
  const languageCode = i18n.language || currentLanguage || 'en';
  const selectedLanguage = availableLanguages.find(
    (lang) => lang.code === languageCode
  )
    ? languageCode
    : 'en';

  const onEditClickHandler = () => {
    setIsEditMode(true);
    setEditedPersonalDetails(cloneDeep(personalDetails));
  };

  const onCancelClickHandler = () => {
    setIsEditMode(false);
    setEditedPersonalDetails(null);
  };

  const onSaveClickHandler = () => {
    setIsEditMode(false);
    // save the edited personal details to the backend
    // set the response to the personal details as new personal details
    setEditedPersonalDetails(null);
  };

  if (!personalDetails && !isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>
          {translateText('profile.personalDetails.noDetailsFound')}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{translateText('common.loading')}</Typography>
      </Box>
    );
  }

  if (!personalDetails) {
    return null;
  }

  const getFullName = (
    details: IProfilePersonalDetailsApiResponse['basicDetails']['primaryDetails']
  ) => {
    const { firstName, middleName, lastName } = details;
    return [firstName, middleName, lastName].filter(Boolean).join(' ');
  };

  const getInitials = (
    details: IProfilePersonalDetailsApiResponse['basicDetails']['primaryDetails']
  ) => {
    const { firstName, lastName } = details;
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}` || '?';
  };

  const getProfilePictureUrl = () => {
    const profilePicture =
      personalDetails.basicDetails.primaryDetails.profile?.profilePicture;
    return profilePicture?.thumbnailUrl || profilePicture?.fileUrl || '';
  };

  const getPhoneNumber = () => {
    const { countryCode, phoneNumber } =
      personalDetails.basicDetails.contactDetails.phone;
    return `${countryCode} ${phoneNumber}`;
  };

  const getAddress = () => {
    const { address1, address2, city, state, country, postalCode } =
      personalDetails.basicDetails.addressDetails;
    const parts = [address1, address2, city, state, country, postalCode].filter(
      Boolean
    );
    return parts.join(', ') || translateText('profile.personalDetails.nA');
  };

  const getGenderLabel = () => {
    const gender = personalDetails.basicDetails.primaryDetails.gender;
    if (!gender) return translateText('profile.personalDetails.nA');
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return translateText('profile.personalDetails.nA');
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    changeLanguage(event.target.value);
  };

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      data-testid="personal-details-container"
      sx={{ pb: { xs: 2, sm: 4 } }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'flex-end',
          gap: { xs: 1, sm: 2 },
        }}
      >
        {isEditMode ? (
          <>
            <CancelButton
              label={translateText('common.cancel')}
              onClickHandler={onCancelClickHandler}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minHeight: { xs: 44, sm: 36 },
              }}
              data-testid="cancel-button"
            />
            <SaveButton
              label={translateText('common.save')}
              onClickHandler={onSaveClickHandler}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minHeight: { xs: 44, sm: 36 },
              }}
              data-testid="save-button"
            />
          </>
        ) : (
          <EditButton
            label={translateText('common.edit')}
            onClickHandler={onEditClickHandler}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: { xs: 44, sm: 36 },
            }}
            data-testid="edit-button"
          />
        )}
      </Grid>

      {isEditMode ? (
        <EditPersonalDetailsView />
      ) : (
        <>
          {/* Basic Details - Primary Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: { xs: 1, sm: 1.5 },
                  }}
                  data-testid="primary-information-title"
                >
                  <Person color="primary" />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    {translateText(
                      'profile.personalDetails.primaryInformation'
                    )}
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        mb: { xs: 1, sm: 1.5 },
                      }}
                    >
                      {/* <Typography variant="caption" color="textSecondary">
                        {translateText('profile.personalDetails.profilePicture')}
                      </Typography> */}
                      <Avatar
                        src={getProfilePictureUrl()}
                        sx={{
                          width: { xs: 64, sm: 80 },
                          height: { xs: 64, sm: 80 },
                          bgcolor: 'primary.main',
                        }}
                        data-testid="profile-picture-avatar"
                      >
                        {getInitials(
                          personalDetails.basicDetails.primaryDetails
                        )}
                      </Avatar>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1, sm: 6 },
                        mb: { xs: 1, sm: 1.5 },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {translateText('profile.personalDetails.fullName')}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {getFullName(
                            personalDetails.basicDetails.primaryDetails
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{
                            display: 'block',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        >
                          {translateText('profile.personalDetails.gender')}
                        </Typography>
                        <Chip
                          label={getGenderLabel()}
                          size="small"
                          color={
                            personalDetails.basicDetails.primaryDetails
                              .gender === GENDER.MALE
                              ? 'primary'
                              : personalDetails.basicDetails.primaryDetails
                                    .gender === GENDER.FEMALE
                                ? 'secondary'
                                : 'default'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {translateText('profile.personalDetails.firstName')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {personalDetails.basicDetails.primaryDetails.firstName}
                    </Typography>
                  </Grid>
                  {personalDetails.basicDetails.primaryDetails.middleName && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {translateText('profile.personalDetails.middleName')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {personalDetails.basicDetails.primaryDetails.middleName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {translateText('profile.personalDetails.lastName')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {personalDetails.basicDetails.primaryDetails.lastName}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Details */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: { xs: 1, sm: 1.5 },
                  }}
                  data-testid="contact-information-title"
                >
                  <Phone color="primary" />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    {translateText(
                      'profile.personalDetails.contactInformation'
                    )}
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                      }}
                      data-testid="email-address-title"
                    >
                      <Email fontSize="small" color="action" />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      >
                        {translateText('profile.personalDetails.emailAddress')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {
                          personalDetails.basicDetails.contactDetails.email
                            .email
                        }
                      </Typography>
                      {personalDetails.basicDetails.contactDetails.email
                        .isVerified && (
                        <Chip
                          label={translateText(
                            'profile.personalDetails.verified'
                          )}
                          size="small"
                          color="success"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1, sm: 1.5 },
                      }}
                      data-testid="phone-number-title"
                    >
                      <Phone fontSize="small" color="action" />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      >
                        {translateText('profile.personalDetails.phoneNumber')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {getPhoneNumber()}
                      </Typography>
                      {personalDetails.basicDetails.contactDetails.phone
                        .isVerified && (
                        <Chip
                          label={translateText(
                            'profile.personalDetails.verified'
                          )}
                          size="small"
                          color="success"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1, sm: 1.5 },
                      }}
                      data-testid="address-title"
                    >
                      <LocationOn fontSize="small" color="action" />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      >
                        {translateText('profile.personalDetails.address')}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {getAddress()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Emergency Contact */}
          {personalDetails.basicDetails.primaryDetails.emergencyContact && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: { xs: 1, sm: 1.5 },
                    }}
                    data-testid="emergency-contact-title"
                  >
                    <Phone color="primary" />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      {translateText(
                        'profile.personalDetails.emergencyContact'
                      )}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translateText('profile.personalDetails.name')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {getFullName(
                          personalDetails.basicDetails.primaryDetails
                            .emergencyContact
                        )}
                      </Typography>
                    </Grid>
                    {personalDetails.basicDetails.primaryDetails
                      .emergencyContact.relationship && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">
                          {translateText(
                            'profile.personalDetails.relationship'
                          )}
                        </Typography>
                        <Typography variant="body1">
                          {
                            personalDetails.basicDetails.primaryDetails
                              .emergencyContact.relationship
                          }
                        </Typography>
                      </Grid>
                    )}
                    {personalDetails.basicDetails.primaryDetails
                      .emergencyContact.phone && (
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                          data-testid="emergency-phone-title"
                        >
                          <Phone fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {translateText(
                              'profile.personalDetails.phoneNumber'
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {
                              personalDetails.basicDetails.primaryDetails
                                .emergencyContact.phone.countryCode
                            }{' '}
                            {
                              personalDetails.basicDetails.primaryDetails
                                .emergencyContact.phone.phoneNumber
                            }
                          </Typography>
                          {personalDetails.basicDetails.primaryDetails
                            .emergencyContact.phone.isVerified && (
                            <Chip
                              label={translateText(
                                'profile.personalDetails.verified'
                              )}
                              size="small"
                              color="success"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                    {personalDetails.basicDetails.primaryDetails
                      .emergencyContact.email && (
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                          data-testid="emergency-email-title"
                        >
                          <Email fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {translateText(
                              'profile.personalDetails.emailAddress'
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {
                              personalDetails.basicDetails.primaryDetails
                                .emergencyContact.email.email
                            }
                          </Typography>
                          {personalDetails.basicDetails.primaryDetails
                            .emergencyContact.email.isVerified && (
                            <Chip
                              label={translateText(
                                'profile.personalDetails.verified'
                              )}
                              size="small"
                              color="success"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Employment History */}
          {personalDetails.professionalDetails.employmentHistory &&
            personalDetails.professionalDetails.employmentHistory.length >
              0 && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1, sm: 1.5 },
                      }}
                      data-testid="employment-history-title"
                    >
                      <Work color="primary" />
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {translateText(
                          'profile.personalDetails.employmentHistory'
                        )}{' '}
                        (
                        {
                          personalDetails.professionalDetails.employmentHistory
                            .length
                        }
                        )
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {personalDetails.professionalDetails.employmentHistory.map(
                        (employment, index) => (
                          <Grid item xs={12} key={employment.id + index}>
                            <Card
                              variant="outlined"
                              sx={{ bgcolor: 'background.default' }}
                            >
                              <CardContent
                                sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    {employment.designation}
                                  </Typography>
                                  {employment.isCurrent && (
                                    <Chip
                                      label={translateText(
                                        'profile.personalDetails.current'
                                      )}
                                      size="small"
                                      color="success"
                                    />
                                  )}
                                </Box>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {translateText(
                                        'profile.personalDetails.department'
                                      )}
                                    </Typography>
                                    <Typography variant="body2">
                                      {employment.department}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {translateText(
                                        'profile.personalDetails.joiningDate'
                                      )}
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatDate(employment.joiningDate)}
                                    </Typography>
                                  </Grid>
                                  {employment.employmentStatus && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.employmentStatus'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {employment.employmentStatus}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Supervisor */}
          {personalDetails.professionalDetails.supervisor && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: { xs: 1, sm: 1.5 },
                    }}
                    data-testid="supervisor-title"
                  >
                    <SupervisorAccount color="primary" />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      {translateText('profile.personalDetails.supervisor')}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        {translateText('profile.personalDetails.name')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {getFullName(
                          personalDetails.professionalDetails.supervisor
                        )}
                      </Typography>
                    </Grid>
                    {personalDetails.professionalDetails.supervisor
                      .contactDetails?.email && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                          {translateText('profile.personalDetails.email')}
                        </Typography>
                        <Typography variant="body1">
                          {
                            personalDetails.professionalDetails.supervisor
                              .contactDetails.email.email
                          }
                        </Typography>
                      </Grid>
                    )}
                    {personalDetails.professionalDetails.supervisor
                      .contactDetails?.phone && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                          {translateText('profile.personalDetails.phone')}
                        </Typography>
                        <Typography variant="body1">
                          {
                            personalDetails.professionalDetails.supervisor
                              .contactDetails.phone.countryCode
                          }{' '}
                          {
                            personalDetails.professionalDetails.supervisor
                              .contactDetails.phone.phoneNumber
                          }
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Qualifications */}
          {personalDetails.professionalDetails.qualifications &&
            personalDetails.professionalDetails.qualifications.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1, sm: 1.5 },
                      }}
                      data-testid="qualifications-title"
                    >
                      <School color="primary" />
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {translateText(
                          'profile.personalDetails.qualifications'
                        )}{' '}
                        (
                        {
                          personalDetails.professionalDetails.qualifications
                            .length
                        }
                        )
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {personalDetails.professionalDetails.qualifications.map(
                        (qualification, index) => (
                          <Grid item xs={12} key={qualification.id + index}>
                            <Card
                              variant="outlined"
                              sx={{ bgcolor: 'background.default' }}
                            >
                              <CardContent
                                sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  {qualification.degree}
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {translateText(
                                        'profile.personalDetails.institution'
                                      )}
                                    </Typography>
                                    <Typography variant="body2">
                                      {qualification.institution}
                                    </Typography>
                                  </Grid>
                                  {qualification.specialization && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.specialization'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {qualification.specialization}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {qualification.passingYear && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.passingYear'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {qualification.passingYear}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {qualification.gradeOrPercentage && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.gradeOrPercentage'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {qualification.gradeOrPercentage}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Certifications */}
          {personalDetails.professionalDetails.certifications &&
            personalDetails.professionalDetails.certifications.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: { xs: 1, sm: 1.5 },
                      }}
                      data-testid="certifications-title"
                    >
                      <VerifiedUser color="primary" />
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {translateText(
                          'profile.personalDetails.certifications'
                        )}{' '}
                        (
                        {
                          personalDetails.professionalDetails.certifications
                            .length
                        }
                        )
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {personalDetails.professionalDetails.certifications.map(
                        (certification, index) => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={certification.id + index}
                          >
                            <Card
                              variant="outlined"
                              sx={{ bgcolor: 'background.default' }}
                            >
                              <CardContent
                                sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  {certification.name}
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      {translateText(
                                        'profile.personalDetails.authority'
                                      )}
                                    </Typography>
                                    <Typography variant="body2">
                                      {certification.authority}
                                    </Typography>
                                  </Grid>
                                  {certification.issuedOn && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.issuedOn'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatDate(certification.issuedOn)}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {certification.validTill && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                      >
                                        {translateText(
                                          'profile.personalDetails.validTill'
                                        )}
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatDate(certification.validTill)}
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                        )
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Teaching Profile */}
          {personalDetails.professionalDetails.teachingProfile && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: { xs: 1, sm: 1.5 },
                    }}
                    data-testid="teaching-profile-title"
                  >
                    <School color="primary" />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      }}
                    >
                      {translateText('profile.personalDetails.teachingProfile')}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    {personalDetails.professionalDetails.teachingProfile
                      .subjects &&
                      personalDetails.professionalDetails.teachingProfile
                        .subjects.length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">
                            {translateText('profile.personalDetails.subjects')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {personalDetails.professionalDetails.teachingProfile.subjects.map(
                              (subject, index) => (
                                <Chip
                                  key={index}
                                  label={subject}
                                  size="small"
                                />
                              )
                            )}
                          </Box>
                        </Grid>
                      )}
                    {personalDetails.professionalDetails.teachingProfile
                      .grades &&
                      personalDetails.professionalDetails.teachingProfile.grades
                        .length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">
                            {translateText('profile.personalDetails.grades')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {personalDetails.professionalDetails.teachingProfile.grades.map(
                              (grade, index) => (
                                <Chip key={index} label={grade} size="small" />
                              )
                            )}
                          </Box>
                        </Grid>
                      )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {translateText('profile.personalDetails.classTeacher')}
                      </Typography>
                      <Typography variant="body1">
                        {personalDetails.professionalDetails.teachingProfile
                          .isClassTeacher
                          ? translateText('profile.personalDetails.yes')
                          : translateText('profile.personalDetails.no')}
                      </Typography>
                    </Grid>
                    {personalDetails.professionalDetails.teachingProfile
                      .isClassTeacher &&
                      personalDetails.professionalDetails.teachingProfile
                        .classTeacherOf &&
                      personalDetails.professionalDetails.teachingProfile
                        .classTeacherOf.length > 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">
                            {translateText(
                              'profile.personalDetails.classTeacherOf'
                            )}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {personalDetails.professionalDetails.teachingProfile.classTeacherOf.map(
                              (classId, index) => (
                                <Chip
                                  key={index}
                                  label={classId}
                                  size="small"
                                  color="primary"
                                />
                              )
                            )}
                          </Box>
                        </Grid>
                      )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* App Preferences */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: { xs: 1, sm: 1.5 },
                  }}
                  data-testid="app-preferences-title"
                >
                  <Settings color="primary" />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    {translateText('profile.personalDetails.appPreferences')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                      }}
                      data-testid="language-title"
                    >
                      <Language fontSize="small" color="action" />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {translateText('profile.personalDetails.language')}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mb: 1, display: 'block', ml: 4 }}
                    >
                      {translateText(
                        'profile.personalDetails.languageDescription'
                      )}
                    </Typography>
                    <Box sx={{ ml: { xs: 0, sm: 4 } }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="app-language-select-label">
                          {translateText('profile.personalDetails.language')}
                        </InputLabel>
                        <Select
                          labelId="app-language-select-label"
                          id="app-language-select"
                          value={selectedLanguage}
                          label={translateText(
                            'profile.personalDetails.language'
                          )}
                          onChange={handleLanguageChange}
                          sx={{
                            minHeight: { xs: 44, sm: 40 },
                            fontSize: { xs: '16px', sm: '14px' },
                          }}
                          data-testid="app-language-select"
                        >
                          {availableLanguages.map((lang) => (
                            <MenuItem
                              key={lang.code}
                              value={lang.code}
                              sx={{ minHeight: { xs: 44, sm: 36 } }}
                            >
                              {lang.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default PersonalDetailsView;
